import { ArrowLeftIcon } from '@heroicons/react/outline';
import { Button } from 'components/atoms/Button';
import { AnimatePresence, motion } from 'motion/react';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { logAmplitude } from 'utils/amplitude';
import { WEBAPP_BANKING_PAYMENTTRANSFER_VIEWEDTRANSFERFORM } from 'utils/amplitude/events/bank.amplitude';
import { fadeTransition } from 'utils/constants';
import AccountSelectionStep from './components/AccountSelectionStep';
import TransferFormStep from './components/TransferFormStep';
import TransferReviewStep from './components/TransferReviewStep';
import { useTransferState } from './hooks/useTransferState';
import {
  TransferStep,
  type TransferFormFields,
  type TransferReviewData,
  type TransferSchedule,
  type MoneyTransferInitialData,
} from './types';
import type { Transaction } from 'types/transactions.types';
import {
  transformReviewDataToFormFields,
  transformScheduleToFormFields,
  transformToReviewStepData,
} from './utils/dataTransforms';
import { updateTransferSchedule } from './utils/transferExecution';

interface MoneyTransferProps {
  initialData: MoneyTransferInitialData;
  onTransferSuccess?: (transaction?: Transaction) => void;
  isStandingOrderPayment?: boolean;
}
const MoneyTransfer: FC<MoneyTransferProps> = ({
  initialData,
  onTransferSuccess,
  isStandingOrderPayment = false,
}) => {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState<TransferStep>(
    TransferStep.ACCOUNT_SELECTION,
  );
  const [reviewData, setReviewData] = useState<TransferReviewData | null>(null);
  const [preservedFormData, setPreservedFormData] = useState<
    TransferFormFields | undefined
  >(undefined);

  const {
    sourceAccount,
    receiptAccount,
    sourceAccountOptions,
    receiptAccountOptions,
    handleSourceChange,
    handleRecipientChange,
    canProceed,
    insufficientBalance,
  } = useTransferState(initialData);

  const isInternalTransfer = receiptAccount !== 'external';

  const handleNext = () => {
    if (currentStep === TransferStep.ACCOUNT_SELECTION) {
      setCurrentStep(TransferStep.TRANSFER_FORM);
    } else if (currentStep === TransferStep.TRANSFER_FORM) {
      setCurrentStep(TransferStep.REVIEW);
    }
  };

  const handleReview = (data: TransferReviewData) => {
    setReviewData(data);

    // Preserve form data for potential return to form step
    if (receiptAccount === 'external') {
      setPreservedFormData(transformReviewDataToFormFields(data));
    }

    setCurrentStep(TransferStep.REVIEW);
  };

  const handleScheduleChange = (scheduleData: TransferSchedule) => {
    // Update the review data with new schedule information
    if (reviewData) {
      const updatedReviewData = updateTransferSchedule(
        reviewData,
        scheduleData,
        sourceAccount?._id,
      );
      setReviewData(updatedReviewData);
    }

    // Update the preserved form data so if user goes back to form, it has the new schedule
    if (preservedFormData) {
      setPreservedFormData({
        ...preservedFormData,
        ...transformScheduleToFormFields(scheduleData),
      });
    }
  };

  const handleBack = () => {
    if (currentStep === TransferStep.TRANSFER_FORM) {
      setCurrentStep(TransferStep.ACCOUNT_SELECTION);
    } else if (currentStep === TransferStep.REVIEW) {
      setCurrentStep(TransferStep.TRANSFER_FORM);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case TransferStep.ACCOUNT_SELECTION:
        return (
          <AccountSelectionStep
            sourceAccount={sourceAccount}
            receiptAccount={receiptAccount}
            sourceAccountOptions={sourceAccountOptions}
            receiptAccountOptions={receiptAccountOptions}
            onSourceAccountChange={handleSourceChange}
            onReceiptAccountChange={handleRecipientChange}
            onNext={handleNext}
            onBack={handleBack}
            canProceed={!!canProceed}
            insufficientBalance={insufficientBalance}
          />
        );

      case TransferStep.TRANSFER_FORM:
        if (!sourceAccount || !receiptAccount) {
          // This shouldn't happen due to canProceed validation
          return null;
        }

        return (
          <TransferFormStep
            sourceAccount={sourceAccount}
            receiptAccount={receiptAccount}
            initialData={initialData}
            isInternalTransfer={isInternalTransfer}
            onTransferSuccess={onTransferSuccess}
            onBack={handleBack}
            onNext={handleNext}
            onReview={handleReview}
            isStandingOrderPayment={isStandingOrderPayment}
            preservedFormData={preservedFormData}
          />
        );

      case TransferStep.REVIEW: {
        if (!sourceAccount || !receiptAccount || !reviewData) {
          return null;
        }

        const reviewStepData = transformToReviewStepData(
          reviewData,
          sourceAccount,
          receiptAccount,
          isInternalTransfer,
          isStandingOrderPayment,
        );

        return (
          <TransferReviewStep
            data={reviewStepData}
            onConfirm={() => {
              if (reviewData?.execution?.onConfirm) {
                reviewData.execution.onConfirm();
              } else {
                console.error('onConfirm function is undefined', {
                  reviewData,
                });
              }
            }}
            onCancel={handleBack}
            isLoading={reviewData.execution?.isLoading}
            onScheduleChange={
              isInternalTransfer ? undefined : handleScheduleChange
            }
          />
        );
      }

      default:
        return null;
    }
  };

  // Log amplitude event
  logAmplitude(WEBAPP_BANKING_PAYMENTTRANSFER_VIEWEDTRANSFERFORM);

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        {currentStep > TransferStep.ACCOUNT_SELECTION && (
          <motion.div
            className="absolute z-10 top-2 left-5"
            {...fadeTransition}
          >
            <Button
              asDefault
              className="flex !items-center space-x-2 mb-5 group"
              onClick={handleBack}
            >
              <ArrowLeftIcon className="h-4 text-primary-500 group-hover:text-primary-700" />
              <span className="text-primary-500 text-sm font-semibold group-hover:text-primary-700">
                {t('back')}
              </span>
            </Button>
          </motion.div>
        )}

        {renderStep()}
      </AnimatePresence>
    </div>
  );
};

export default MoneyTransfer;
